import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { renderTemplate } from "../lib/templates";
import { Button } from "./ui/button";

const WIDTHS = { desktop: 640, mobile: 375 } as const;

/**
 * Quick preview of a user template without entering the editor (§C.2) — the
 * live rendered HTML in a width-toggle iframe, with a shortcut into the editor.
 * Mirrors GalleryPreviewModal but sources HTML from renderTemplate(id).
 */
export function TemplatePreviewModal({
  templateId,
  name,
  onClose,
}: {
  templateId: string;
  name: string;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [width, setWidth] = useState<keyof typeof WIDTHS>("desktop");

  const preview = useQuery({
    queryKey: ["template", "render", templateId],
    queryFn: () => renderTemplate(templateId),
    staleTime: 60_000,
  });

  // Close on Escape — modal a11y.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${name}`}
      className="fixed inset-0 z-50 flex flex-col bg-black/50 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="mx-auto flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <p className="truncate text-sm font-medium text-neutral-900">{name}</p>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-neutral-200 p-0.5" role="group" aria-label="Preview width">
              {(["desktop", "mobile"] as const).map((w) => (
                <button
                  key={w}
                  type="button"
                  aria-pressed={width === w}
                  onClick={() => setWidth(w)}
                  className={`rounded px-2 py-1 text-xs capitalize ${
                    width === w ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
            <Button variant="primary" size="sm" onClick={() => navigate(`/templates/${templateId}/edit`)}>
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={onClose} aria-label="Close preview">
              Close
            </Button>
          </div>
        </header>
        <div className="flex-1 overflow-auto bg-neutral-100 p-4">
          {preview.isLoading ? (
            <div className="mx-auto h-96 animate-pulse rounded bg-neutral-200" style={{ width: WIDTHS[width] }} />
          ) : preview.isError || !preview.data ? (
            <p role="alert" className="py-12 text-center text-sm text-error-700">
              Couldn't render this template's preview.
            </p>
          ) : (
            <iframe
              title={`Preview of ${name}`}
              srcDoc={preview.data}
              sandbox=""
              className="mx-auto block h-[70vh] border-0 bg-white shadow-sm"
              style={{ width: WIDTHS[width] }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
